// Modern 2024/2025 color palette - eco/fresh theme with vibrant accents
// Inspired by nature, sustainability, and modern tech aesthetics

export const lightTheme = {
  // Core colors
  primary: "#10B981", // Emerald green - fresh, eco
  primaryDark: "#059669",
  accent: "#F59E0B", // Amber - warm contrast
  accentLight: "#FBBF24",
  
  // Brand color (teal heritage)
  brand: "#0D9488",
  brandLight: "#14B8A6",
  brandDark: "#0F766E",
  
  // Backgrounds
  background: "#F0FDF4", // Very light mint
  background2: "#FFFFFF",
  backgroundGlass: "rgba(255, 255, 255, 0.7)",
  backgroundCard: "#FFFFFF",
  backgroundMuted: "#ECFDF5",
  
  // Gradients
  gradientPrimary: "linear-gradient(135deg, #10B981 0%, #0D9488 100%)",
  gradientHeader: "linear-gradient(135deg, #0F766E 0%, #134E4A 100%)",
  gradientAccent: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
  gradientSubtle: "linear-gradient(180deg, #F0FDF4 0%, #ECFDF5 100%)",
  
  // Text
  text: "#134E4A", // Dark teal
  textSecondary: "#64748B",
  textMuted: "#94A3B8",
  textLight: "#94A3B8",
  textReverse: "#FFFFFF",
  
  // Links & interactive
  link: "#0D9488",
  header: "#0F766E",
  
  // Borders & separators
  border: "#D1FAE5",
  borderCard: "#E2E8F0",
  itemBorderColor: "#E2E8F0",
  
  // Calendar
  calendarCellHeader: "#0D9488",
  calendarCellCurrent: "#F59E0B",
  
  // Status colors
  red: "#EF4444",
  success: "#10B981",
  warning: "#F59E0B",
  
  // Shadows
  shadowSm: "0 1px 2px rgba(16, 185, 129, 0.05)",
  shadowMd: "0 4px 6px -1px rgba(16, 185, 129, 0.1), 0 2px 4px -2px rgba(16, 185, 129, 0.1)",
  shadowLg: "0 10px 15px -3px rgba(16, 185, 129, 0.1), 0 4px 6px -4px rgba(16, 185, 129, 0.1)",
  shadowXl: "0 20px 25px -5px rgba(16, 185, 129, 0.1), 0 8px 10px -6px rgba(16, 185, 129, 0.1)",
  shadowGlow: "0 0 40px rgba(16, 185, 129, 0.15)",
  
  // Navbar
  navBackground: "rgba(255, 255, 255, 0.85)",
  navBorder: "rgba(16, 185, 129, 0.1)",
  navShadow: "0 -4px 30px rgba(0, 0, 0, 0.05)",
  navActive: "#0D9488",
  navInactive: "#64748B",
  
  // Mode identifier
  mode: "light" as const,
};

export const darkTheme = {
  // Core colors
  primary: "#34D399", // Lighter emerald for dark mode
  primaryDark: "#10B981",
  accent: "#FBBF24", // Brighter amber
  accentLight: "#FCD34D",
  
  // Brand color
  brand: "#2DD4BF",
  brandLight: "#5EEAD4",
  brandDark: "#14B8A6",
  
  // Backgrounds - rich dark with subtle teal undertones
  background: "#0C1B19", // Deep dark teal
  background2: "#122825", // Card background
  backgroundGlass: "rgba(18, 40, 37, 0.8)",
  backgroundCard: "#1A3330",
  backgroundMuted: "#0F2420",
  
  // Gradients
  gradientPrimary: "linear-gradient(135deg, #34D399 0%, #2DD4BF 100%)",
  gradientHeader: "linear-gradient(135deg, #134E4A 0%, #0C1B19 100%)",
  gradientAccent: "linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)",
  gradientSubtle: "linear-gradient(180deg, #0C1B19 0%, #0F2420 100%)",
  
  // Text
  text: "#E2E8F0", // Light gray
  textSecondary: "#94A3B8",
  textMuted: "#64748B",
  textLight: "#64748B",
  textReverse: "#0C1B19",
  
  // Links & interactive
  link: "#2DD4BF",
  header: "#34D399",
  
  // Borders & separators
  border: "#1E4D47",
  borderCard: "#234E47",
  itemBorderColor: "#234E47",
  
  // Calendar
  calendarCellHeader: "#2DD4BF",
  calendarCellCurrent: "#FBBF24",
  
  // Status colors
  red: "#F87171",
  success: "#34D399",
  warning: "#FBBF24",
  
  // Shadows
  shadowSm: "0 1px 2px rgba(0, 0, 0, 0.3)",
  shadowMd: "0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -2px rgba(0, 0, 0, 0.3)",
  shadowLg: "0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -4px rgba(0, 0, 0, 0.3)",
  shadowXl: "0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.4)",
  shadowGlow: "0 0 40px rgba(52, 211, 153, 0.1)",
  
  // Navbar
  navBackground: "rgba(12, 27, 25, 0.9)",
  navBorder: "rgba(52, 211, 153, 0.15)",
  navShadow: "0 -4px 30px rgba(0, 0, 0, 0.3)",
  navActive: "#2DD4BF",
  navInactive: "#64748B",
  
  // Mode identifier
  mode: "dark" as const,
};

// Theme type for TypeScript
export type AppTheme = typeof lightTheme;
